import { sectionRenderer } from '@theme/section-renderer';
import { announce, createFocusTrap, focusFirstTabbable, safeFocus } from '@nulo/a11y';

/**
 * @param {URL} url
 * @returns {URL}
 */
function toBrowserUrl(url) {
  const browserUrl = new URL(url.href);
  browserUrl.searchParams.delete('section_id');
  return browserUrl;
}

/**
 * @extends {HTMLElement}
 */
class SearchMain extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {(() => void) | null} */
  #filterTrap = null;

  /** @type {(() => void) | null} */
  #sortTrap = null;

  /** @type {boolean} */
  #initialized = false;

  /** @type {boolean} */
  #isRendering = false;

  connectedCallback() {
    if (this.#initialized) return;

    if (this.#ensureDefaultQuery()) return;

    this.#initialized = true;

    this.#bindEvents();
    this.#syncSortState();
    this.#updateHeaderOffset();
    this.#ensureFiltersLoaded();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#filterTrap?.();
    this.#filterTrap = null;
    this.#sortTrap?.();
    this.#sortTrap = null;
    this.#initialized = false;
  }

  /**
   * Handles two URL normalization cases on page load:
   * 1. Blank query → redirect to the configured default search query (e.g. "dog").
   * 2. Query present but no type param → add type=product.
   * @returns {boolean} True when a redirect was issued (caller should abort further init)
   */
  #ensureDefaultQuery() {
    const url = new URL(window.location.href);
    if (!url.pathname.endsWith('/search')) return false;

    const query = url.searchParams.get('q');

    // Case 1: no query at all — redirect to the configured default
    if (!query) {
      const config = this.#parseConfig();
      const defaultQuery = config?.defaultSearchQuery || '*';
      url.searchParams.set('q', defaultQuery);
      if (!url.searchParams.get('type')) {
        url.searchParams.set('type', 'product');
      }
      window.location.replace(url.toString());
      return true;
    }

    // Case 2: query present but missing type — normalize to product tab
    if (!url.searchParams.get('type')) {
      url.searchParams.set('type', 'product');
      window.location.replace(url.toString());
      return true;
    }

    return false;
  }

  #parseConfig() {
    const el = this.querySelector('[data-config]');
    if (!el?.textContent) return null;

    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #bindEvents() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', (event) => this.#onFilterChange(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });

    for (const select of this.querySelectorAll('[data-search-sort]')) {
      select.addEventListener('change', (event) => this.#onSortChange(event), { signal });
    }

    for (const link of this.querySelectorAll('[data-collection-page]')) {
      link.addEventListener('click', (event) => this.#onPaginationClick(event), { signal });
    }

    for (const drawer of this.querySelectorAll('dialog')) {
      drawer.addEventListener(
        'click',
        (event) => {
          if (event.target === drawer) {
            if (drawer.hasAttribute('data-search-filter-drawer')) {
              this.#closeFilterDrawer();
            } else if (drawer.hasAttribute('data-search-sort-drawer')) {
              this.#closeSortDrawer();
            }
          }
        },
        { signal }
      );
    }

    window.addEventListener(
      'popstate',
      () => {
        this.#renderFromUrl(new URL(window.location.href), { skipPush: true });
      },
      { signal }
    );

    window.addEventListener('resize', () => this.#updateHeaderOffset(), { signal });
  }

  /**
   * @param {Event} event
   */
  #onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-search-filter-open]')) {
      event.preventDefault();
      this.#openFilterDrawer();
      return;
    }

    if (target.closest('[data-search-filter-close]')) {
      event.preventDefault();
      this.#closeFilterDrawer();
      return;
    }

    if (target.closest('[data-search-filter-apply]')) {
      event.preventDefault();
      this.#applyMobileFilters();
      return;
    }

    if (target.closest('[data-search-sort-open]')) {
      event.preventDefault();
      this.#openSortDrawer();
      return;
    }

    if (target.closest('[data-search-sort-close]')) {
      event.preventDefault();
      this.#closeSortDrawer();
      return;
    }

    const sortOption = target.closest('[data-search-sort-option]');
    if (sortOption instanceof HTMLButtonElement) {
      event.preventDefault();
      this.#applySort(sortOption.value);
      this.#closeSortDrawer();
    }
  }

  /**
   * @param {Event} event
   */
  #onFilterChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-search-filter]')) return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;

    const url = this.#buildUrlFromFilters();
    this.#renderFromUrl(url, { focusTarget: '[data-search-grid], [data-search-blog-grid]' });
  }

  /**
   * @param {Event} event
   */
  #onSortChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-search-sort]')) return;
    this.#applySort(select.value);
  }

  /**
   * @param {Event} event
   */
  #onPaginationClick(event) {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    const url = new URL(link.href, window.location.origin);
    this.#renderFromUrl(url, { focusTarget: '[data-search-grid], [data-search-blog-grid]' });
  }

  #applyMobileFilters() {
    const mobileForm = this.querySelector(
      '.collection-filters--mobile [data-search-filters-form]'
    );
    if (!(mobileForm instanceof HTMLFormElement)) return;

    const url = this.#buildUrlFromFilters(mobileForm);
    this.#closeFilterDrawer();
    this.#renderFromUrl(url, { focusTarget: '[data-search-grid], [data-search-blog-grid]' });
  }

  /**
   * @param {HTMLFormElement} [sourceForm]
   * @returns {URL}
   */
  #buildUrlFromFilters(sourceForm) {
    const config = this.#parseConfig();
    const baseUrl = config?.searchUrl || window.location.pathname;
    const url = new URL(baseUrl, window.location.origin);

    const currentParams = new URL(window.location.href).searchParams;
    const query = currentParams.get('q') || config?.searchQuery || '';
    if (query) url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
        url.searchParams.delete(key);
      }
    }

    const forms = sourceForm ? [sourceForm] : [this.#getActiveFilterForm()].filter(Boolean);

    for (const form of forms) {
      if (!(form instanceof HTMLFormElement)) continue;

      const formData = new FormData(form);
      for (const [name, value] of formData.entries()) {
        if (!name.startsWith('filter.')) continue;
        url.searchParams.append(name, String(value));
      }
    }

    const sortSelect = this.querySelector('[data-search-sort]');
    const sortValue =
      sortSelect instanceof HTMLSelectElement ? sortSelect.value : currentParams.get('sort_by');
    if (sortValue && sortValue !== 'relevance') {
      url.searchParams.set('sort_by', sortValue);
    }

    return url;
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getActiveFilterForm() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const desktopForm = this.querySelector('.collection-filters--desktop [data-search-filters-form]');
    const mobileForm = this.querySelector('.collection-filters--mobile [data-search-filters-form]');

    if (isDesktop && desktopForm instanceof HTMLFormElement) return desktopForm;
    if (mobileForm instanceof HTMLFormElement) return mobileForm;
    if (desktopForm instanceof HTMLFormElement) return desktopForm;
    return null;
  }

  /**
   * @param {string} sortValue
   */
  #applySort(sortValue) {
    const url = this.#buildUrlFromFilters(undefined);

    if (sortValue && sortValue !== 'relevance') {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');
    this.#renderFromUrl(url, { focusTarget: '[data-search-grid], [data-search-blog-grid]' });
  }

  /**
   * @param {URL} url
   * @param {{ skipPush?: boolean, focusTarget?: string }} [options]
   */
  async #renderFromUrl(url, options = {}) {
    if (this.#isRendering) return;

    const config = this.#parseConfig();
    if (!config?.sectionId) return;

    this.#isRendering = true;
    const liveRegion = this.querySelector('[data-search-live]');
    if (liveRegion) liveRegion.textContent = config.i18n?.loading || 'Loading';

    try {
      await sectionRenderer.renderSection(String(config.sectionId), {
        url,
        cache: false,
      });

      if (!options.skipPush) {
        history.pushState({}, '', toBrowserUrl(url).toString());
      }

      this.#bindEvents();
      this.#syncSortState();
      this.#updateHeaderOffset();
      await this.#ensureFiltersLoaded();

      if (liveRegion) liveRegion.textContent = config.i18n?.loaded || 'Content loaded';
      announce(config.i18n?.loaded || 'Content loaded', 'polite');

      if (options.focusTarget) {
        const focusEl = this.querySelector(options.focusTarget);
        if (focusEl instanceof HTMLElement) {
          const focused = focusFirstTabbable(focusEl);
          if (!focused) {
            focusEl.setAttribute('tabindex', '-1');
            safeFocus(focusEl);
          }

          const targetToScroll = focused || focusEl;
          const header = document.querySelector('.primary-header, header-component, .header');
          const headerOffset = header instanceof HTMLElement ? header.offsetHeight + 20 : 0;
          const elementPosition = targetToScroll.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        }
      }
    } finally {
      this.#isRendering = false;
    }
  }

  #syncSortState() {
    const currentSort = new URL(window.location.href).searchParams.get('sort_by') || 'relevance';

    for (const select of this.querySelectorAll('[data-search-sort]')) {
      if (select instanceof HTMLSelectElement) {
        select.value = currentSort;
      }
    }

    for (const option of this.querySelectorAll('[data-search-sort-option]')) {
      if (!(option instanceof HTMLButtonElement)) continue;
      option.classList.toggle('is-active', option.value === currentSort);
    }
  }

  #openFilterDrawer() {
    const drawer = this.querySelector('[data-search-filter-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    drawer.showModal();
    this.#filterTrap?.();
    this.#filterTrap = createFocusTrap(drawer, () => this.#closeFilterDrawer());

    const openButton = this.querySelector('[data-search-filter-open]');
    if (openButton instanceof HTMLButtonElement) {
      openButton.setAttribute('aria-expanded', 'true');
    }
  }

  #closeFilterDrawer() {
    const drawer = this.querySelector('[data-search-filter-drawer]');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;

    this.#filterTrap?.();
    this.#filterTrap = null;
    drawer.close();

    const openButton = this.querySelector('[data-search-filter-open]');
    if (openButton instanceof HTMLButtonElement) {
      openButton.setAttribute('aria-expanded', 'false');
      safeFocus(openButton);
    }
  }

  #openSortDrawer() {
    const drawer = this.querySelector('[data-search-sort-drawer]');
    if (!(drawer instanceof HTMLDialogElement)) return;

    drawer.showModal();
    this.#sortTrap?.();
    this.#sortTrap = createFocusTrap(drawer, () => this.#closeSortDrawer());

    const openButton = this.querySelector('[data-search-sort-open]');
    if (openButton instanceof HTMLButtonElement) {
      openButton.setAttribute('aria-expanded', 'true');
    }
  }

  #closeSortDrawer() {
    const drawer = this.querySelector('[data-search-sort-drawer]');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;

    this.#sortTrap?.();
    this.#sortTrap = null;
    drawer.close();

    const openButton = this.querySelector('[data-search-sort-open]');
    if (openButton instanceof HTMLButtonElement) {
      openButton.setAttribute('aria-expanded', 'false');
      safeFocus(openButton);
    }
  }

  #updateHeaderOffset() {
    const header = document.querySelector('nulo-header');
    const bar = header?.querySelector('.nulo-header__bar');
    if (!(bar instanceof HTMLElement)) return;

    const height = Math.round(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  }

  async #ensureFiltersLoaded() {
    const existingGroups = this.querySelectorAll('[data-search-filters-form] details');
    if (existingGroups.length > 0) return;

    const config = this.#parseConfig();
    const rootUrl = config?.rootUrl || window.Shopify?.routes?.root || '/';
    const cleanRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const collectionsUrl = `${cleanRoot}collections/all`;

    try {
      const response = await fetch(collectionsUrl);
      if (!response.ok) return;
      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');

      const desktopSourceForm = doc.querySelector('.collection-filters--desktop [data-collection-filters-form]');
      const mobileSourceForm = doc.querySelector('.collection-filters--mobile [data-collection-filters-form]');

      const desktopTargetPanel = this.querySelector('[data-search-filters-panel="desktop"]');
      const mobileTargetPanel = this.querySelector('[data-search-filters-panel="mobile"]');

      if (desktopSourceForm && desktopTargetPanel) {
        const desktopTargetForm = desktopTargetPanel.querySelector('[data-search-filters-form]');
        if (desktopTargetForm) {
          desktopTargetForm.innerHTML = desktopSourceForm.innerHTML;
          this.#transformFallbackInputs(desktopTargetForm);
        }
      }

      if (mobileSourceForm && mobileTargetPanel) {
        const mobileTargetForm = mobileTargetPanel.querySelector('[data-search-filters-form]');
        if (mobileTargetForm) {
          mobileTargetForm.innerHTML = mobileSourceForm.innerHTML;
          this.#transformFallbackInputs(mobileTargetForm);
        }
      }

      this.#syncActiveFiltersFromUrl();
    } catch (err) {
      console.warn('Could not fetch fallback collection filters:', err);
    }
  }

  /**
   * @param {Element} form
   */
  #transformFallbackInputs(form) {
    const inputs = form.querySelectorAll('input[data-collection-filter]');
    for (const input of inputs) {
      input.removeAttribute('data-collection-filter');
      input.setAttribute('data-search-filter', '');
      if (input instanceof HTMLInputElement) {
        input.disabled = false;
        input.removeAttribute('disabled');
      }
      const item = input.closest('.collection-filters__item');
      if (item) {
        item.classList.remove('collection-filters__item--disabled');
      }
      const label = input.closest('.collection-filters__label');
      if (label) {
        label.classList.remove('collection-filters__label--disabled');
      }
    }
  }

  #syncActiveFiltersFromUrl() {
    const currentParams = new URL(window.location.href).searchParams;
    const inputs = this.querySelectorAll('[data-search-filter]');

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const paramValues = currentParams.getAll(input.name);
      const isChecked = paramValues.includes(input.value);
      input.checked = isChecked;

      const details = input.closest('details');
      if (details && isChecked) {
        details.open = true;
      }
    }

    const hasActive = [...inputs].some(
      (input) => input instanceof HTMLInputElement && input.checked
    );

    const clearLinks = this.querySelectorAll('[data-search-clear]');
    for (const clearLink of clearLinks) {
      if (clearLink instanceof HTMLElement) {
        clearLink.style.display = hasActive ? '' : 'none';
      }
    }
  }
}

if (!customElements.get('search-main')) {
  customElements.define('search-main', SearchMain);
}
